import { useCallback, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { ArrowDownTrayIcon } from '@heroicons/react/20/solid';

import { showError } from '../../../lib/utils';
import { Button } from '../../_core/button/button.component';
import { NO_FILES_ERROR, useDownloadAllAssets } from '../assets/asset.service';
import { EditorApolloProvider } from '../../_editor/_core/editor-apollo-provider';
import { UploadData, UploadFileType } from '../../_editor/upload/upload.types';
import { IRequest, IRequestBlock, IRequestOverview, IRequestTokenParams, ITemplate } from '../../../lib/types';
import { EditorSaveResult, SaveProps } from '../../_editor/_core/types';
import { determineLocalTitle } from '../../_editor/_core/local-title-block/local-title-block.utils';
import { RIDialog } from '../../_core/dialog/dialog.component';
import { DownloadRequestAssetsContainer } from '../assets/download-request-assets/download-request-assets.container';
import { findFilesToDownload } from './request.utils';
import { ISectionData } from '../../_editor/section/section.types';

interface IDownloadRequestAssetsButtonContainer extends IRequestTokenParams {
  activeSection?: number | null;
  disabled?: boolean;
  editorData: ISectionData[];
  forceLabelsOnDocProvide?: boolean; // Force use labels on doc provide downloads
  ignoreSpacing?: boolean;
  onSave: (_?: SaveProps) => Promise<EditorSaveResult>;
  request?: IRequest;
  template?: ITemplate;
}

interface IDownloadRequestAssetsButton extends Omit<IDownloadRequestAssetsButtonContainer, 'request' | 'template'> {
  containerName: string;
  hasFiles: boolean;
  request?: IRequestOverview;
}

const DownloadRequestAssetsButton = ({
  activeSection,
  containerName,
  disabled,
  editorData,
  forceLabelsOnDocProvide,
  hasFiles,
  ignoreSpacing,
  onSave,
  request,
  requestToken
}: IDownloadRequestAssetsButton) => {
  const [showDownloadRequestAssets, setShowDownloadRequestAssets] = useState(false);

  const { downloadAssets } = useDownloadAllAssets({ activeSection, requestToken });

  const handleDownloadAssets = useCallback(
    async (fileType?: UploadFileType) => {
      try {
        const { data: savedData } = await onSave({ forceFetchLatestData: true });
        if (savedData) {
          const blocks = savedData.blocks as IRequestBlock[];
          const hasActiveSection = typeof activeSection === 'number';

          const items = findFilesToDownload({
            blocks: hasActiveSection && blocks.length > activeSection ? [blocks[activeSection]] : blocks,
            fileType,
            forceLabelsOnDocProvide
          });

          try {
            await downloadAssets({
              items,
              archiveName: `${fileType === 'docProvide' ? 'provided-files_' : 'uploaded-files_'}${containerName}${
                hasActiveSection ? `_${determineLocalTitle(1, activeSection)}` : ''
              }`
            });
          } catch (err) {
            if ((err as Error).message === NO_FILES_ERROR) showError(NO_FILES_ERROR, err as Error);
            else showError('Failed to download files', err as Error);
          }
        } else toast.error('Failed to find files to download', { id: 'skipped-download' });
      } catch (err) {
        showError('Failed to download files', err as Error);
      }
    },
    [onSave, activeSection, forceLabelsOnDocProvide, downloadAssets, containerName]
  );

  return (
    <>
      <Button
        slim
        className={ignoreSpacing ? undefined : 'ml-4'}
        hideEndMargin
        icon={<ArrowDownTrayIcon height={24} width={24} />}
        size="fit"
        fit="right"
        variant="secondary"
        disabled={(!hasFiles && !request) || disabled}
        dropdown={
          request
            ? undefined
            : {
                options: [
                  { text: 'Download provided files', onClick: () => handleDownloadAssets('docProvide') },
                  { text: 'Download uploaded files', onClick: () => handleDownloadAssets('upload') }
                ],
                hideIcon: true,
                ignoreRelativeContainer: true,
                className: 'max-w-52'
              }
        }
        onClick={request ? () => setShowDownloadRequestAssets(true) : undefined}
      />
      {!!request && (
        <RIDialog open={showDownloadRequestAssets} setOpen={setShowDownloadRequestAssets}>
          <DownloadRequestAssetsContainer
            editorData={editorData}
            requestOverview={request}
            close={() => setShowDownloadRequestAssets(false)}
            requestToken={requestToken}
          />
        </RIDialog>
      )}
    </>
  );
};

export const DownloadRequestAssetsButtonContainer = ({
  onSave,
  request,
  template,
  ...rest
}: IDownloadRequestAssetsButtonContainer) => {
  const hasFiles = useMemo(() => {
    const findFileToDownload = (data?: object | null): boolean => {
      return (
        !!data &&
        Object.entries(data).some(([key, value]) => {
          if (key === 'uploads') {
            return !!(data as UploadData).uploads.length;
          } else if (typeof value === 'object') {
            return findFileToDownload(value);
          }

          return false;
        })
      );
    };

    const blocks = request?.blocks ?? template?.blocks;
    if (!blocks) return false;

    return !!blocks?.some(({ data }) => findFileToDownload(data));
  }, [request?.blocks, template?.blocks]);

  return (
    <EditorApolloProvider>
      <DownloadRequestAssetsButton
        {...rest}
        hasFiles={hasFiles}
        onSave={onSave}
        containerName={request?.name ?? template?.title ?? 'assets'}
        request={request}
      />
    </EditorApolloProvider>
  );
};
